import { EnumSurveyQuestionType, type SurveyByIdQuery } from "gql/generated";
import { isLexicalHtmlEmpty } from "shared/components/molecules/TextEditor";
import {
  getDefaultSurveyQuestionResponse,
  isBooleanResponseAnswered,
} from "shared/components/organisms/SurveyQuestionInput/surveyQuestionCheckbox";
import { isSurveyDesignFieldType } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionDesign";
import {
  clearHiddenResponses,
  isQuestionVisible,
  isToggleQuestionType,
} from "shared/components/organisms/SurveyQuestionInput/surveyQuestionVisibility";
import type { QuestionnaireResponse } from "widgets/booker/contexts";

export type DynamicFeeSurvey = NonNullable<SurveyByIdQuery["surveyById"]>;
export type DynamicFeeSurveyQuestion = DynamicFeeSurvey["questions"][number];

export const getDynamicFeeSurveyId = (
  product?: {
    sellingConfig?: { dynamicFeeSurvey?: string | null } | null;
  } | null,
) => product?.sellingConfig?.dynamicFeeSurvey ?? null;

/** Question ids that some pricing option's condition depends on. */
export const getPricingConditionQuestionIds = (
  options: Array<{
    condition?: {
      clauses?: Array<{ questionId?: string | null } | null> | null;
    } | null;
  } | null>,
) =>
  new Set(
    options.flatMap(option =>
      (option?.condition?.clauses ?? [])
        .map(clause => clause?.questionId)
        .filter((id): id is string => !!id),
    ),
  );

/**
 * Questions a price depends on are treated as required: without an answer no
 * conditional option can match, so checkout could never complete.
 */
export const getActiveDynamicFeeQuestions = (
  survey?: DynamicFeeSurvey | null,
  pricingQuestionIds?: Set<string>,
) =>
  (survey?.questions ?? [])
    .filter(q => q.isActive && !isSurveyDesignFieldType(q.type))
    .map(q =>
      !q.required && pricingQuestionIds?.has(q._id)
        ? { ...q, required: true }
        : q,
    );

export const getDefaultResponseForQuestion = (
  question: DynamicFeeSurveyQuestion,
) => getDefaultSurveyQuestionResponse(question);

export const mergeDynamicFeeResponses = (
  allResponses: QuestionnaireResponse[],
  survey: DynamicFeeSurvey,
  changedQuestionId: string,
  response: string[],
) => {
  const activeQuestions = getActiveDynamicFeeQuestions(survey);
  const dynamicQuestionIds = new Set(activeQuestions.map(q => q._id));
  const previousDynamicResponses = allResponses.filter(r =>
    dynamicQuestionIds.has(r.questionData._id),
  );

  const nextDynamicResponses = clearHiddenResponses(
    activeQuestions.map(question => {
      const existing = previousDynamicResponses.find(
        r => r.questionData._id === question._id,
      );

      return {
        questionData: question,
        response:
          question._id === changedQuestionId
            ? response
            : (existing?.response ?? getDefaultResponseForQuestion(question)),
      };
    }),
  );

  return [
    ...allResponses.filter(r => !dynamicQuestionIds.has(r.questionData._id)),
    ...nextDynamicResponses,
  ];
};

export const areRequiredDynamicFeeQuestionsAnswered = (
  questions: DynamicFeeSurveyQuestion[],
  responses: QuestionnaireResponse[],
) =>
  questions.every(question => {
    if (
      !question.isActive ||
      !question.required ||
      isSurveyDesignFieldType(question.type) ||
      isToggleQuestionType(question.type) ||
      !isQuestionVisible(question, responses)
    ) {
      return true;
    }

    const response = responses.find(r => r.questionData._id === question._id);
    if (!response) return false;

    if (question.type === EnumSurveyQuestionType.Boolean) {
      return isBooleanResponseAnswered(question, response.response);
    }

    if (question.type === EnumSurveyQuestionType.RichText) {
      return !isLexicalHtmlEmpty(response.response?.[0]);
    }

    return response.response.some(value => (value ?? "").trim().length > 0);
  });
